"use client";

import React, { useState, useEffect } from "react";
import { FlaskConical, ShieldCheck, FileCheck2, Sparkles, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
declare const wvcClient: {
  getCurrentWPQueryParams?: () => Record<string, string | undefined>;
};
const trustHighlights = [{
  icon: ShieldCheck,
  title: "HPLC purity verified",
  description: "≥99.2% analytical purity"
}, {
  icon: FileCheck2,
  title: "Batch documentation",
  description: "COA included with every order"
}, {
  icon: FlaskConical,
  title: "Institutional grade",
  description: "In-vitro laboratory research use only"
}];
const categoryPills = [{
  label: "All products",
  to: "/ShopPage"
}, {
  label: "Research peptides",
  to: "/ShopPage"
}, {
  label: "Peptide blends",
  to: "/ShopPage"
}, {
  label: "Analytical data",
  to: "/ResearchInformationPage"
}];
export default function ShopHero() {
  const [searchTerm, setSearchTerm] = useState<string>("");
  useEffect(() => {
    try {
      const params = typeof wvcClient !== "undefined" && typeof wvcClient.getCurrentWPQueryParams === "function" ? wvcClient.getCurrentWPQueryParams() : {};
      const query = String(params?.s ?? params?.search ?? "").trim();
      setSearchTerm(query);
    } catch {
      setSearchTerm("");
    }
  }, []);
  const isSearch = Boolean(searchTerm);
  const headingText = isSearch ? `Search results for "${searchTerm}"` : "Research peptide catalogue";
  return <section id="shop-hero" data-nav="light" className="relative bg-background text-foreground border-b border-border py-12 md:py-16">
      <div className="container mx-auto px-4 md:px-6 max-w-6xl">
        {/* Compliance and laboratory context kicker */}
        <div className="flex flex-wrap items-center gap-2 mb-4 text-xs font-mono tracking-wider uppercase text-muted-foreground">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm bg-muted border border-border text-foreground font-semibold">
            <span className="inline-block size-1.5 rounded-full bg-primary" />
            RUO Compliance
          </span>
          <span>Verified laboratory reagents</span>
          <span className="text-border">|</span>
          <span>Fast domestic dispatch across India</span>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
          <div className="lg:col-span-8">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-default font-semibold tracking-tight text-foreground mb-4">
              {headingText}
            </h1>
            <p className="text-base sm:text-lg text-muted-foreground leading-relaxed max-w-2xl font-sans mb-6">
              {isSearch ? `Showing available laboratory-grade reagents matching your search query. Each batch is supplied with lot-specific chromatographic verification.` : "Browse analytical-grade reagents and custom synthesis lots for institutional laboratories across India. Every vial is shipped under verified cold-chain conditions with complete analytical documentation."}
            </p>

            {/* Quick Filter Navigation Pills */}
            <div className="flex flex-wrap items-center gap-2 pt-1">
              {categoryPills.map((pill, i) => <div key={pill.label} data-index={i}>
                  <Link to={pill.to} className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md border border-border bg-card text-foreground hover:border-primary hover:text-primary transition-colors">
                    {pill.label}
                  </Link>
                </div>)}
            </div>
          </div>

          {/* Secondary Action / Documentation Link */}
          <div className="lg:col-span-4 flex flex-col sm:flex-row lg:flex-col gap-3 justify-start lg:items-end">
            <Button asChild variant="outline" className="w-full sm:w-auto text-xs uppercase tracking-wide">
              <Link to="/ResearchInformationPage" className="inline-flex items-center justify-center gap-2">
                <span>View analytical specifications</span>
                <ArrowRight className="size-3.5" />
              </Link>
            </Button>
            <Button asChild variant="default" className="w-full sm:w-auto text-xs uppercase tracking-wide">
              <Link to="/ContactPage" className="inline-flex items-center justify-center gap-2">
                <span>Custom synthesis inquiry</span>
              </Link>
            </Button>
          </div>
        </div>

        {/* Analytical Verification Bar */}
        <div className="mt-12 pt-8 border-t border-border grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-6">
          {trustHighlights.map((item, i) => {
          const IconComponent = item.icon;
          return <div key={item.title} data-index={i} className="flex items-start gap-3 p-3.5 rounded-md bg-muted/50 border border-border/80">
                <div className="p-2 rounded-sm bg-card border border-border text-primary shrink-0">
                  <IconComponent className="size-4" />
                </div>
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wider text-foreground font-sans">
                    {item.title}
                  </div>
                  <div className="text-xs text-muted-foreground font-mono mt-0.5">
                    {item.description}
                  </div>
                </div>
              </div>;
        })}
        </div>
      </div>
    </section>;
}